import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/EmptyState";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtDate, fmtNum, addDaysISO, todayISO } from "@/lib/format";

export default function Audit() {
  const { projectId } = useProjectCtx();
  const [filters, setFilters] = useState({ category: "ALL", date_from: addDaysISO(todayISO(), -30), date_to: todayISO() });
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: filters.date_from, date_to: filters.date_to, limit, offset };
      if (filters.category !== "ALL") params.category = filters.category;
      const { data } = await api.get(`/projects/${projectId}/progress-entries`, { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [projectId, filters, offset]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="audit-page">
      <PageHeader
        title="Audit Trail"
        description="Every progress update, who made it, and when."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.category} onValueChange={(v) => { setOffset(0); setFilters({ ...filters, category: v }); }}>
              <SelectTrigger className="w-40" data-testid="audit-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All categories</SelectItem>
                <SelectItem value="ENGINEERING">Engineering</SelectItem>
                <SelectItem value="PROCUREMENT">Procurement</SelectItem>
                <SelectItem value="CONSTRUCTION">Construction</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={filters.date_from} onChange={(e) => { setOffset(0); setFilters({ ...filters, date_from: e.target.value }); }} data-testid="audit-date-from-input" className="w-36" />
            <Input type="date" value={filters.date_to} onChange={(e) => { setOffset(0); setFilters({ ...filters, date_to: e.target.value }); }} data-testid="audit-date-to-input" className="w-36" />
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && rows.length === 0 ? (
          <EmptyState title="No progress entries in range" testId="audit-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Metric</TableHead><TableHead>Quantity</TableHead><TableHead>Value date</TableHead><TableHead>Status</TableHead><TableHead>Note</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(r.entry_date)}</TableCell>
                  <TableCell className="text-xs text-foreground">{r.category}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.metric.replaceAll("_", " ")}</TableCell>
                  <TableCell className="font-mono text-xs">{r.quantity !== null ? fmtNum(r.quantity) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(r.value_date)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.status_value || "—"}</TableCell>
                  <TableCell className="max-w-xs text-xs text-muted-foreground">{r.note || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(offset - limit, 0))} data-testid="audit-prev-page-button">Previous</Button>
        <Button size="sm" variant="outline" disabled={rows.length < limit} onClick={() => setOffset(offset + limit)} data-testid="audit-next-page-button">Next</Button>
      </div>
    </div>
  );
}
